import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope, useAtExit } from './answer-ref-context';
import { answerRefSections } from './answer-ref-picker-menu';
import { useBlockIdentity } from './fired-states';
import { FlowPickerMenu } from './flow-picker-menu';
import { variableSections } from './variable-picker-menu';

import type { PickerHandle } from './flow-picker-menu';
import type { TaskVariable } from '@/types/experience';
import type { AnswerRef } from '@agavi/blocks';

import { useCapabilities } from '@/hooks/use-capabilities';

/**
 * ONE BUTTON FOR BOTH THINGS A FORMULA CAN HOLD, because the field has room for one.
 *
 * The toolbar version of this is two buttons — braces for a variable, brackets for an earlier
 * answer — and in a toolbar that is right: there is width to spend and naming each is clearer
 * than making a teacher open a menu to find out what is in it.
 *
 * An equation input is a single inline row with the field itself competing for the same pixels.
 * Two buttons there cost a visible slice of the thing being typed in, and stacking them was the
 * other idea and a worse one: a half-height target is below what a finger can hit.
 *
 * So one button, one menu, two groups inside it — which is nearly free, because both pickers
 * already build `PickerSection[]` and already render through `FlowPickerMenu`. Nothing new is
 * invented here; two lists are concatenated.
 *
 * ## The BUTTON shows both; a typed character does not
 *
 * The button is an undirected "insert something", so it owes you everything it can insert. A
 * typed `{` or `[` is not undirected: the character IS the request. Typing `[` and being shown
 * the variables first, with the answers somewhere below, reads as the menu ignoring what was
 * just typed — and the teacher who typed it already knew which half they wanted.
 *
 * So `show` narrows the same menu rather than there being three menus. One component, one key
 * namespace, one `onChoose`.
 *
 * ## The keys have to stay apart
 *
 * A variable's key is its NAME; an answer's is `blockId:facet`. Prefixing both is what keeps a
 * variable called `mcq1:value` from inserting an answer — contrived, but the kind of collision
 * that is free to prevent now and expensive to find later.
 */
const VAR = 'v:';
const REF = 'r:';

export function FormulaInsertMenu({
  variables, onVariable, onRef, handleRef, onDismiss, returnFocus, show = 'both',
  refsWithheld = false, varsWithheld = false,
}: {
  variables: TaskVariable[];
  onVariable: (name: string) => void;
  onRef: (ref: AnswerRef) => void;
  /** Which half to offer. `both` is the button; the typed shortcuts name their own. */
  show?: 'both' | 'variables' | 'refs';
  /**
   * This field cannot hold a recalled answer AT ALL — distinct from `show`, which is the
   * momentary narrowing a typed character applies.
   *
   * Only the permanent fact may touch the trigger. Deriving the glyph from `show` instead made
   * the button flicker from `{]` to `{}` for as long as a typed `{` menu was open, which is a
   * button changing what it claims to do while the teacher is mid-gesture.
   */
  refsWithheld?: boolean;
  /**
   * The MIRROR of `refsWithheld`: this block may not take a variable at all, because the
   * Variables capability has not reached its rung yet.
   *
   * Permanent in the same sense — a teacher cannot type their way out of it from here — so it
   * is allowed to touch the trigger where an empty `variables` array is not. A task with no
   * variables authored yet still promises `{]`, and rightly: the half is reachable, just empty,
   * and the answer is to go and write one.
   */
  varsWithheld?: boolean;
  handleRef?: PickerHandle extends never ? never : Parameters<typeof FlowPickerMenu>[0]['handleRef'];
  /** Dismissed without choosing — the caller puts back the character the shortcut swallowed. */
  onDismiss?: () => void;
  /** Puts the caret back in the equation, which is where the teacher was. */
  returnFocus?: () => void;
}) {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  const identity = useBlockIdentity();
  const atExit = useAtExit();
  /* The rung's source half, so this menu offers what the toolbar picker offers. */
  const { level } = useCapabilities();
  const sourceRung = level('answerRecall');
  /*
   * WHAT THE BUTTON SAYS IT OPENS has to match what is inside it.
   *
   * `{]` is one brace and one bracket precisely because the menu holds both; in a field that
   * withholds the recall half it advertises something the teacher will not find, and the
   * bracket is the signifier they would then go looking for. `{}` is the variables-only
   * signifier the toolbar has always used.
   *
   * The accessible NAME moves with it. "Variable or earlier answer" on a menu offering only
   * variables is the same lie told to a screen-reader user, and a louder one — they cannot
   * see the list to discover the second half is missing.
   *
   * It cuts BOTH ways, which is what the Variables rungs made visible: in a Tilt block below
   * the rung, with recall available, the button went on promising a brace that opened onto
   * nothing. `[]` is that same correction pointed the other way — the bracket alone, because
   * the bracket alone is what this field can now hold.
   */
  /**
   * Neither half: an insert button over an empty menu, which is a button for nothing. Checked
   * FIRST, and that is what keeps the other two one-liners — past this point `varsWithheld`
   * already implies the recall half survived, so neither flag needs to re-ask.
   */
  const nothingToInsert = varsWithheld && (refsWithheld || !scope);
  const varsOnly = refsWithheld || !scope;
  const refsOnly = varsWithheld;

  /*
   * A typed character narrows the menu, but a withheld half cannot be narrowed BACK INTO by one.
   * Forcing the surviving half here rather than trusting `show` is the same repair the formula
   * field already needed for `refScope`: the two look equivalent and are not.
   */
  const shown = varsWithheld ? 'refs' : show;

  const sections = useMemo(() => {
    const vars = shown === 'refs' ? [] : variableSections(variables, t).map(s => ({
      ...s, options: s.options.map(o => ({ ...o, key: `${VAR}${o.key}` })),
    }));
    // No scope means no task to pull an answer from, so the menu is the variables alone — the
    // same gate the button itself uses.
    const refs = scope && shown !== 'variables'
      // `identity?.blockId` so a formula cannot quote the answer of the block it is computing
      // — see `answerRefSections`. The rest of the current task is offered, as it is everywhere.
      ? answerRefSections(scope.tasks, scope.currentTaskId, t, scope.aliases, undefined, 'number',
        { atExit, selfBlockId: identity?.blockId }, sourceRung)
        .map(s => ({ ...s, options: s.options.map(o => ({ ...o, key: `${REF}${o.key}` })) }))
      : [];
    return [...vars, ...refs];
  }, [variables, scope, t, shown, identity?.blockId, atExit, sourceRung]);

  // Nothing in either half: no trigger at all, rather than a button onto an empty list.
  if (nothingToInsert) return null;

  const pickerName = varsOnly
    ? t('blocks.toolbar.variables')
    : refsOnly ? t('blocks.toolbar.answerRef') : t('blocks.toolbar.insertPicker');

  return (
    <FlowPickerMenu
      sections={sections}
      ariaLabel={pickerName}
      noMatchesLabel={t('blocks.toolbar.noInsertMatch')}
      filterPlaceholder={t('blocks.toolbar.filterInsert')}
      handleRef={handleRef}
      onDismiss={onDismiss}
      returnFocus={returnFocus}
      className='flex shrink-0 self-stretch'
      onChoose={(key) => {
        if (key.startsWith(VAR)) { onVariable(key.slice(VAR.length)); return; }
        // The block and facet are in the key; the task is recovered by looking the block up,
        // rather than stuffing a third id into a string two places would have to parse.
        const [blockId, facet] = key.slice(REF.length).split(':');
        const task = scope?.tasks.find(tk => (tk.blocks ?? []).some(b => b.id === blockId));
        if (!task || !blockId || !facet) return;
        onRef({ taskId: task.id, blockId, facet });
      }}
      renderTrigger={p => (
        <button
          {...p}
          type='button'
          aria-label={pickerName}
          className='flex shrink-0 items-center self-stretch border-l border-ws-border bg-ws-primary/10 dark:bg-ws-text-link/10 px-2.5 text-ws-primary dark:text-ws-text-link transition-colors hover:bg-ws-primary/20 dark:hover:bg-ws-text-link/20'
        >
          {/*
            * `{]` — one brace and one bracket, because the button opens both and neither
            * symbol alone would say so. The two characters are already the signifiers: a
            * brace starts a variable in this field, and a bracket is what the recall chip
            * writes when it lands somewhere that cannot hold one.
            *
            * With one half withheld the pair closes onto whichever survives — `{}` or `[]` —
            * so the glyph names what the menu actually holds.
            *
            * Text rather than a lucide glyph, and the only one in this bar that is. There is
            * no icon for "both of these", and inventing a drawing for it would be a third
            * thing to learn on top of the two characters a teacher already types.
            */}
          <span aria-hidden='true' className='font-mono text-[0.8125rem] leading-none tracking-tighter'>
            {varsOnly ? '{}' : refsOnly ? '[]' : '{]'}
          </span>
        </button>
      )}
    />
  );
}
